import { Application, Blocks } from '@/types/application';
import { BackendNode } from '@/types/backendNode';
import { ComparerBlocks, ComparerNode, ComparerParam } from '@/types/comparer';
import { Connection, IndexPortOption } from '@/types/connection';
import { Detail } from '@/types/detail';
import { EventHandler } from '@/types/eventhandler';
import { Block, Configuration, ConfigurationEnum, Metadata } from '@/types/metadata/metadata';
import { VariableRef } from '@/types/variable';
import { isArray, isConfigurationEnum, isConfigurationMultiple, isObject, isVariable } from '@/util/typeGuards';
import { flattenValue, makeParam } from './comparerUtils';

const findConfigurationLabel = (
  configuration: Configuration[] | ConfigurationEnum[] | undefined,
  key: string
): string | undefined => {
  if (!configuration) return;

  for (const config of configuration) {
    if (config.value === key) return config.label;

    if (isConfigurationMultiple(config)) {
      const sectionLabel = findConfigurationLabel(config.sections, key);
      if (sectionLabel) return sectionLabel;
    }

    if (isConfigurationEnum(config)) {
      for (const section of config.sections ?? []) {
        const sectionLabel = findConfigurationLabel(section, key);
        if (sectionLabel) return sectionLabel;
      }
    }
  }

  return;
};

const comparePortOptions = (
  portoptionA: Record<string, IndexPortOption>,
  portoptionB: Record<string, IndexPortOption>,
  nodeId: number
): ComparerParam[] => {
  const result: ComparerParam[] = [];
  const portA = portoptionA[nodeId];
  const portB = portoptionB[nodeId];

  if (!portA && !portB) return [];

  result.push(makeParam('↱ index', portA?.index, portB?.index));
  result.push(makeParam('↱ label', portA?.label, portB?.label));

  return result;
};

const compareDetails = (
  detailsA: Detail | undefined,
  detailsB: Detail | undefined,
  metaBlock: Block | undefined
): ComparerParam[] => {
  const result: ComparerParam[] = [];
  const allDetailsKeys = new Set([...Object.keys(detailsA ?? {}), ...Object.keys(detailsB ?? {})]);

  allDetailsKeys.forEach((key) => {
    if (key && key[0] === '_') return;

    const valueA = detailsA?.[key];
    const valueB = detailsB?.[key];
    const label = findConfigurationLabel(metaBlock?.configuration, key) ?? key;

    result.push(compareDetail(valueA, valueB, label));
  });

  return result;
};

const compareDetail = (detailA: unknown, detailB: unknown, key: string): ComparerParam => {
  const valueA = isObject(detailA) || isArray(detailA) ? JSON.stringify(detailA) : detailA;
  const valueB = isObject(detailB) || isArray(detailB) ? JSON.stringify(detailB) : detailB;

  return makeParam(key, valueA, valueB);
};

type ConnectionPair = { connA?: Connection; connB?: Connection };

// Pairs the two revisions on action, then lines up whatever is left over positionally so a renamed
// action reads as a change rather than a removal plus an addition.
const pairConnections = (connectionsA: Connection[], connectionsB: Connection[]): ConnectionPair[] => {
  const unpairedB = [...(connectionsB ?? [])];

  const pairs: ConnectionPair[] = (connectionsA ?? []).map((connA) => {
    const match = connA.action ? unpairedB.findIndex((connB) => connB.action === connA.action) : -1;
    return { connA: connA, connB: match === -1 ? undefined : unpairedB.splice(match, 1)[0] };
  });

  pairs.forEach((pair) => {
    if (!pair.connB && unpairedB.length) pair.connB = unpairedB.shift();
  });

  return [...pairs, ...unpairedB.map((connB) => ({ connB: connB }))];
};

const compareConnections = (
  connectionsA: Connection[],
  connectionsB: Connection[],
  nodesA: Record<string, BackendNode>,
  nodesB: Record<string, BackendNode>
): ComparerParam[] => {
  if (!connectionsA && !connectionsB) return [];

  const result: ComparerParam[] = [];

  pairConnections(connectionsA, connectionsB).forEach(({ connA, connB }) => {
    const dstLabelA = connA?.dst && nodesA[connA.dst] ? `#${connA.dst} ${nodesA[connA.dst].type}` : '';
    const dstLabelB = connB?.dst && nodesB[connB.dst] ? `#${connB.dst} ${nodesB[connB.dst].type}` : '';

    if (!connA) {
      result.push(makeParam(`↳ ${connB?.label || ''} (action)`, '', connB?.action, true));
      result.push(makeParam(`↳ ${connB?.label || ''} (dst)`, '', dstLabelB, true));
    } else if (!connB) {
      result.push(makeParam(`↳ ${connA.label || ''} (action)`, connA.action, '', true));
      result.push(makeParam(`↳ ${connA.label || ''} (dst)`, dstLabelA, '', true));
    } else {
      if (connA.action !== connB.action)
        result.push(makeParam(`↳ ${connB.label} (action)`, connA.action, connB.action, true));
      if (connA.label !== connB.label)
        result.push(makeParam(`↳ ${connB.label} (label)`, connA.label, connB.label, true));

      const dstChanged =
        connA.dst !== connB.dst || (connA.dst && connB.dst && nodesA[connA.dst]?.type !== nodesB[connB.dst]?.type);
      result.push(
        makeParam(`↳ ${connB.label} (dst)`, dstLabelA || connA.dst, dstLabelB || connB.dst, Boolean(dstChanged))
      );
    }
  });

  return result;
};

const compareEventHandlers = (
  eventHandlersA: Record<string, Record<string, EventHandler>> | undefined,
  eventHandlersB: Record<string, Record<string, EventHandler>> | undefined,
  nodeId: number
): ComparerParam[] => {
  const result: ComparerParam[] = [];
  const eventHandlerA = eventHandlersA?.[nodeId];
  const eventHandlerB = eventHandlersB?.[nodeId];

  if (!eventHandlerA && !eventHandlerB) return [];

  const ehA = Object.keys(eventHandlerA ?? {});
  const ehB = Object.keys(eventHandlerB ?? {});
  const allHandlerIds: Set<string> = new Set([...ehA, ...ehB]);

  allHandlerIds.forEach((handlerId) => {
    const handlerA = eventHandlerA?.[handlerId];
    const handlerB = eventHandlerB?.[handlerId];

    result.push(makeParam(`${handlerId} (eventstart)`, handlerA?.eventstart, handlerB?.eventstart));
    result.push(makeParam(`${handlerId} (name)`, handlerA?.name, handlerB?.name));
    const handlerArgs = compareArguments(handlerA?.arguments ?? [], handlerB?.arguments ?? [], handlerId);
    handlerArgs.length && result.push(...handlerArgs);
  });

  return result;
};

const compareArguments = (
  argumentsA: (VariableRef | number | string)[],
  argumentsB: (VariableRef | number | string)[],
  handlerId: string
): ComparerParam[] => {
  const result: ComparerParam[] = [];
  const mapA = new Map(argumentsA.map((argument) => [isVariable(argument) ? argument.name : argument, argument]));
  const mapB = new Map(argumentsB.map((argument) => [isVariable(argument) ? argument.name : argument, argument]));
  const allIds = new Set([...mapA.keys(), ...mapB.keys()]);

  allIds.forEach((id) => {
    const argA = mapA.get(id);
    const argB = mapB.get(id);
    const flattenedArgA = flattenValue(argA);
    const flattenedArgB = flattenValue(argB);
    if (!argA) result.push(makeParam(`${handlerId} (${id})`, '', flattenedArgB, true));
    else if (!argB) result.push(makeParam(`${handlerId} (${id})`, flattenedArgA, '', true));
    else result.push(makeParam(`${handlerId} (${id})`, flattenedArgA, flattenedArgB));
  });

  return result;
};

const compareBlocks = (blocksA: Blocks, blocksB: Blocks, nodeId: number, metadata: Metadata): ComparerBlocks => {
  const changes: ComparerBlocks = {
    node: [],
    portoptions: [],
    connections: [],
    details: [],
    event_handlers: []
  };

  const nodeType = blocksB.nodes[nodeId]?.type ?? blocksA.nodes[nodeId]?.type;
  const metaBlock = metadata.blocks.find((block) => block.type === nodeType);

  changes['node'].push(makeParam('Type', blocksA.nodes[nodeId]?.type, blocksB.nodes[nodeId]?.type));
  changes['node'].push(makeParam('Label', blocksA.nodes[nodeId]?.label, blocksB.nodes[nodeId]?.label));
  const details = compareDetails(blocksA.details[nodeId], blocksB.details[nodeId], metaBlock);
  const portoptions = comparePortOptions(blocksA.portoptions, blocksB.portoptions, nodeId);
  const eventHandlers = compareEventHandlers(blocksA.event_handlers, blocksB.event_handlers, nodeId);
  const connections = compareConnections(
    blocksA.connections[nodeId],
    blocksB.connections[nodeId],
    blocksA.nodes,
    blocksB.nodes
  );

  changes['details'] = details;
  changes['portoptions'] = portoptions;
  changes['event_handlers'] = eventHandlers;
  changes['connections'] = connections;

  return changes;
};

export const compareNodes = (
  nodesA: number[],
  nodesB: number[],
  appA: Application,
  appB: Application,
  metadata: Metadata
): ComparerNode[] => {
  const nodeChanges: ComparerNode[] = [];
  const allNodes = new Set([...nodesA, ...nodesB]);

  allNodes.forEach((node) => {
    const nodeA = appA.blocks.nodes[node];
    const nodeB = appB.blocks.nodes[node];

    if (!nodesA.includes(node)) {
      nodeChanges.push({
        status: 'added',
        id: `#${node}`,
        label: nodeB?.label ? `${nodeB.type} (${nodeB.label})` : nodeB?.type || node.toString(),
        changedBlocks: compareBlocks(appA.blocks, appB.blocks, node, metadata)
      });
    } else if (!nodesB.includes(node)) {
      nodeChanges.push({
        status: 'removed',
        id: `#${node}`,
        label: nodeA?.label ? `${nodeA.type} (${nodeA.label})` : nodeA?.type || node.toString(),
        changedBlocks: compareBlocks(appA.blocks, appB.blocks, node, metadata)
      });
    } else {
      const changes = compareBlocks(appA.blocks, appB.blocks, node, metadata);
      const nodeHasChanges = Object.values(changes)
        .flat()
        .filter((param) => param.changed).length;

      nodeChanges.push({
        status: nodeHasChanges ? 'modified' : 'unchanged',
        id: `#${node}`,
        label: nodeA?.label ? `${nodeA.type} (${nodeA.label})` : nodeA?.type || node.toString(),
        changedBlocks: changes
      });
    }
  });

  return nodeChanges;
};
